import {Measurement} from 'constants/MeasurementConstants';
import {FullyAccessibleGrid} from 'constants/SampleConstants';
import {MinimalSchemaTag} from 'folder-items/FileConstants';
import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {
  AnnotateFixedPlateWellInput,
  FixedPlateTransferInputWell,
  PlateMapTestOrderInput,
  PlateMapTransferContentInput,
  PlateMapTransferSourceInput,
  ProcedureParameterValue,
} from 'samples/GridDAO';
import {
  FrozenTestOrderInfo,
  PlateMapAnnotateInput,
  PlateMapPositionInput,
  PlateMapTransferInput,
} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {
  BioentityContent,
  BioentitySource,
  MeasurementInput,
  PlateMap,
  PlateMapWell,
  PlateMaterialInput,
  ReagentType,
  SUPPORTED_FIELD_TYPES_FOR_ANNOTATION,
  TestOrder,
} from 'samples/plate-maps/PlateMapConstants';
import {hasWellBeenModified} from 'samples/plate-maps/PlateMapUtil';
import {NO_MATERIAL_INPUT_SENTINEL} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {getTagUpdateParams} from 'schemas/tags/TagUtil';

/**
 * Get update params from for the given MinimalSchemaTag.
 * This is just a wrapper around getTagUpdateParams that converts empty string inputs to null.
 */
export function getSchemaTagUpdateParams(tag: MinimalSchemaTag): Partial<MinimalSchemaTag> {
  const {fieldType} = tag.tagSchemaField;
  if (fieldType === FieldType.FLOAT) {
    return {
      // @ts-expect-error Float schema tags are a special case because they include both
      // a numerical value (floatValue) and a string value (value) with the user's full input.
      // In order to maintain both, the backend expects this {floatValue: <string>} format.
      // However if the there is no value, the backend expects null instead of an empty string.
      floatValue: tag.value == null || tag.value.length === 0 ? null : tag.value,
    };
  }
  if (fieldType === FieldType.DATE) {
    // TODO(BNCH-12786): For some unknown reason dates don't use dateValue... :shrug:
    return {dateValue: tag.value};
  }
  return getTagUpdateParams(tag);
}

export function getPlateMapTransferInput(well: PlateMapWell): NonNullable<PlateMapTransferInput> {
  const contents: Array<PlateMapTransferContentInput> = well.contents.map((content) => ({
    entity_id: content.id,
    final_concentration: toMeasurementInput(content.finalConcentration.measurement),
    material_input: getMaterialInputForBioentitySourceOrContent(content),
    test_orders: getTestOrderInfosForEntity(content.testOrders),
  }));
  const sources: Array<PlateMapTransferSourceInput> = well.sources.map((source) => {
    if (source.type === ReagentType.SOURCE_CONTAINABLE) {
      return {
        entity_id: source.id,
        final_concentration: toMeasurementInput(source.finalConcentration.measurement),
        source_concentration: toMeasurementInput(source.sourceConcentration.measurement),
        transfer_quantity: toMeasurementInput(source.transferQuantity.measurement),
        material_input: getMaterialInputForBioentitySourceOrContent(source),
        test_orders: getTestOrderInfosForEntity(source.testOrders),
      };
    } else {
      return {
        container_id: source.id,
        contents: source.contents.map((content) => ({
          entity_id: content.id,
          final_concentration: toMeasurementInput(content.finalConcentration.measurement),
          material_input: getMaterialInputForBioentitySourceOrContent(content),
          test_orders: getTestOrderInfosForEntity(content.testOrders),
        })),
        transfer_quantity: toMeasurementInput(source.transferQuantity.measurement),
      };
    }
  });
  return {
    sources,
    contents,
    final_quantity: toMeasurementInput(well.finalQuantity.measurement),
  };
}

export function getPlateMapPositionInput(
  well: PlateMapWell,
  annotatedDataParameterIds: Set<string> | null,
  annotatedSchemaFieldIds: Set<string>
): PlateMapPositionInput {
  return {
    coordinates: well.position,
    annotate_input: getAnnotatePlateMapWellInput({
      well,
      annotatedDataParameterIds,
      annotatedSchemaFieldIds,
    }),
    transfer_input: getPlateMapTransferInput(well),
  };
}

/**
 * Get annotation input parameters for the given plate map in the shape expected by the API.
 * @param annotatedSchemaFieldIds Well schema fields to filter to for annotation.
 * @deprecated Migrate to PlateMap*Input
 */
export function getAnnotateFixedPlateInputsFromPlateMap({
  plateMap,
  annotatedDataParameterIds,
  annotatedSchemaFieldIds,
}: {
  plateMap: PlateMap;
  annotatedDataParameterIds: Set<string> | null;
  annotatedSchemaFieldIds: Set<string>;
}): Array<AnnotateFixedPlateWellInput> {
  return plateMap.wells.map((well) => ({
    ...getAnnotatePlateMapWellInput({
      well,
      annotatedDataParameterIds,
      annotatedSchemaFieldIds,
    }),
    position: well.position,
  }));
}

/**
 * Get annotation input parameters for the given plate map well in the shape expected by the API.
 *
 * Note that we omit position and leave that to the caller since the position is
 * represented differently between a well/position in a PlateDesign vs PlateRecord.
 *
 * @param annotatedDataParameterIds Data parameter tags to filter for annotation. If null, all data parameters
 *    are included.
 * @param annotatedSchemaFieldIds Well schema fields to filter to for annotation.
 *    If null, all schema fields are included.
 */
export function getAnnotatePlateMapWellInput({
  well,
  annotatedDataParameterIds,
  annotatedSchemaFieldIds,
}: {
  well: {
    schemaTags: {[fieldId: string]: MinimalSchemaTag};
    dataParameterTags: {[versionId: string]: MinimalSchemaTag};
    role: ExperimentalRole | null;
  };
  annotatedDataParameterIds: Set<string> | null;
  annotatedSchemaFieldIds: Set<string> | null;
}): NonNullable<PlateMapAnnotateInput> {
  const schemaFieldValues: {[apiId: string]: Partial<MinimalSchemaTag>} = {};
  for (const [fieldId, tag] of Object.entries(well.schemaTags).filter(
    ([fieldId, _]) => annotatedSchemaFieldIds == null || annotatedSchemaFieldIds.has(fieldId)
  )) {
    schemaFieldValues[fieldId] = getSchemaTagUpdateParams(tag);
  }
  const parameters = Object.entries(well.dataParameterTags)
    .filter(([_, tag]) => SUPPORTED_FIELD_TYPES_FOR_ANNOTATION.has(tag.tagSchemaField.fieldType))
    .filter(([versionId, _]) => annotatedDataParameterIds == null || annotatedDataParameterIds.has(versionId))
    .map(([versionId, tag]) => ({
      parameter_version_id: versionId,
      schema_field_value_dict: getSchemaTagUpdateParams(tag),
    }));
  const role =
    well.role == null
      ? null
      : {primary_role: well.role.primaryRole, subrole: well.role.subrole, group: well.role.group};
  return {role, schema_field_values: schemaFieldValues, parameters};
}

const toMeasurementInput = (measurement: Measurement): MeasurementInput => ({
  value: measurement.value,
  unit:
    measurement.unit == null
      ? null
      : {unit_api_id: measurement.unit.id, unit_version_number: measurement.unit.versionNumber},
});

export function getMaterialInputForBioentity(
  bioentityId: string,
  materialInputId: string
): ProcedureParameterValue {
  // Sentinel value for fields that are needed by the TS shape but not used by getTagUpdateParams.
  const unusedValue = '';

  // Currently we assume that material inputs are always BIOENTITY_LINKs
  const schemaTag: MinimalSchemaTag = {
    tagSchemaField: {name: unusedValue, fieldType: FieldType.BIOENTITY_LINK},
    name: unusedValue,
    targetFiles: [{id: bioentityId, name: unusedValue, url: unusedValue}],
  };

  return {
    parameter_version_id: materialInputId,
    schema_field_value_dict: getTagUpdateParams(schemaTag),
  };
}

export function getTestOrderInfosForEntity(
  testOrders: Array<TestOrder> | Array<FrozenTestOrderInfo> | null | undefined
): Array<PlateMapTestOrderInput> | null {
  if (testOrders == null) {
    return null;
  }
  const isFrozenTestOrder = (testOrder: TestOrder | FrozenTestOrderInfo): testOrder is FrozenTestOrderInfo =>
    (testOrder as FrozenTestOrderInfo).testOrderId != null;
  return testOrders.map((testOrder) => ({
    test_order_id: isFrozenTestOrder(testOrder) ? testOrder.testOrderId : testOrder.id,
  }));
}

function getMaterialInputForBioentitySourceOrContent(
  bioentity: BioentitySource | BioentityContent
): ProcedureParameterValue | null {
  if (
    bioentity.materialInput != null &&
    bioentity.materialInput.materialInputId !== NO_MATERIAL_INPUT_SENTINEL
  ) {
    const materialInputId = bioentity.materialInput.materialInputId;
    return getMaterialInputForBioentity(bioentity.id, materialInputId);
  } else {
    return null;
  }
}

/**
 * Get transfer input parameters for the given plate map in the shape expected by the API.
 *
 * Keep in-sync with `getPlateDesignInputFromPlateDesignForSave`.
 *
 * @deprecated Migrate to PlateMap*Input
 */
export function getTransferToFixedPlateInputsFromPlateMap(
  plateMap: PlateMap
): Array<FixedPlateTransferInputWell> {
  return plateMap.wells.map((well) => ({
    position: well.position,
    ...getPlateMapTransferInput(well),
  }));
}

/**
 * Get the API IDs of all containers that are potentially modified by a full plate transfer,
 * given the exisiting grid and the plate map containing draft transfer source information.
 * If filterUnmodifiedWells is true, destination wells that are not modified in the transfer are not included.
 */
export function getModifiedStorableIdsForTransfer(
  plateMap: PlateMap,
  grid: FullyAccessibleGrid | null,
  filterUnmodifiedWells: boolean
): Array<string> {
  let modifiedPlateWellIds: Array<string> = [];
  if (grid != null) {
    let modifiedPlateWells = plateMap.wells;
    if (filterUnmodifiedWells) {
      modifiedPlateWells = plateMap.wells.filter((well) => hasWellBeenModified(well));
    }
    modifiedPlateWellIds = modifiedPlateWells.map(
      // All wells in a well plate should have a non nullish ID
      (well) => grid.cells[well.position.row][well.position.column]!.container.id
    );
  }
  const sourceContainerIds = new Set(
    plateMap.wells
      .flatMap((well) =>
        well.sources.map((source) => (source.type === ReagentType.SOURCE_CONTAINER ? source.id : null))
      )
      .filter((id): id is string => id != null)
  );
  return [...modifiedPlateWellIds, ...sourceContainerIds];
}

export function getAverageContentsAddedPerWell(plateMap: PlateMap): number {
  const allSourceContentsCount = plateMap.wells
    .flatMap((well) => well.sources)
    .reduce(
      (sum, reagent) => sum + (reagent.type === ReagentType.SOURCE_CONTAINABLE ? 1 : reagent.contents.length),
      0
    );
  return allSourceContentsCount / plateMap.wells.length;
}

/**
 * Given a map of material inputs by id, construct a map of entity IDs to the material input ID that should be
 * autopopulated based on planned values.
 *
 * If an entity is associated with multiple material inputs, pick the one which is in step. If there is still
 * ambiguity, return null.
 */
export function getAutopopulatedMaterialInputIdMap(plateMaterialInputsById: {
  [id: string]: PlateMaterialInput;
}): {[entityId: string]: string | null} {
  const materialInputsByPlannedValueId: Record<string, Array<PlateMaterialInput>> = {};
  for (const materialInput of Object.values(plateMaterialInputsById)) {
    for (const plannedValue of materialInput.plannedValues) {
      if (plannedValue.targetFiles == null || plannedValue.targetFiles.length === 0) {
        continue;
      }
      // Material input schemas are always single valued, so the planned value will have just one file.
      const entityId = plannedValue.targetFiles[0].id;
      materialInputsByPlannedValueId[entityId] ||= [];
      materialInputsByPlannedValueId[entityId].push(materialInput);
    }
  }
  return Object.fromEntries(
    Object.entries(materialInputsByPlannedValueId).map(([id, materialInputs]) => {
      if (materialInputs.length === 1) {
        return [id, materialInputs[0].versionId];
      }
      const inStepMaterialInputs = materialInputs.filter((input) => input.inStep);
      if (inStepMaterialInputs.length === 1) {
        return [id, inStepMaterialInputs[0].versionId];
      }
      return [id, null];
    })
  );
}
